import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import View from 'ol/View';
import { create, FeaturePath, multiSelectOptions } from 'sdi/map';
import {
    QAMAPNAME,
    getBaseLayer,
    getView,
    getMapInfo,
    getInteraction,
    getSymbology,
} from '../queries/map';
import { Option, none, some, fromNullable } from 'fp-ts/lib/Option';
import {
    updateView,
    setScaleLine,
    selectIssueFromFeature,
} from '../events/map';
import { DIV, NODISPLAY } from 'sdi/components/elements';
import { mapAndTableButton, mapButton } from './buttons';
import {
    getAutoCenter,
    getAutoZoom,
    getLayout,
    getTheme,
} from '../queries/app';
import {
    changeAutoZoom,
    setAutoCenter,
    toggleRenderMap,
    toggleRenderMapAndTable,
} from '../events/app';
//import baseLayerSwitch from './baselayer-select';
import { getSelectedIssueIds } from '../queries/table';
import { clearSelectedIssues } from '../events/qa';
import { navigateClear } from '../events/route';
import tr from 'sdi/locale';
import { divTooltipLeft } from 'sdi/components/tooltip';
import { clearRowSelectionAll } from '../events/table';
import { Nullable } from 'sdi/util';
import { DEFAULT_CENTER_3857 } from '../shape/map';
import { getSelectedStyle } from '../queries/map/style';

let mapUpdate: Option<() => void> = none;
let mapSetTarget: Option<(e: HTMLElement) => void> = none;

const selectFeatures = (paths: FeaturePath[]) => {
    clearSelectedIssues();
    if (paths.length < 1) {
        clearRowSelectionAll();
        navigateClear();
    } else {
        paths
            .sort((a, b) => (b.featureId as number) - (a.featureId as number))
            .forEach(p =>
                fromNullable(p.featureId).map(fid =>
                    selectIssueFromFeature(fid)
                )
            );
    }
};

const getSelected = (): FeaturePath[] =>
    getSelectedIssueIds().map(issueId => ({
        featureId: issueId,
        layerId:
            getSymbology() === 'typology'
                ? 'issues-typology'
                : 'issues-priority',
    }));

const attachMap = (element: Nullable<HTMLElement>) => {
    mapUpdate = mapUpdate.foldL(
        () => {
            const { update, setTarget, selectable, andThen } = create(
                QAMAPNAME,
                {
                    getBaseLayer,
                    getView,
                    getMapInfo,
                    updateView,
                    setScaleLine,
                    element,
                }
            );
            mapSetTarget = some(setTarget);
            update();
            selectable(
                multiSelectOptions({
                    selectFeatures,
                    getSelected,
                    filter: (_f, l) =>
                        l.get('id') === `issues-${getSymbology()}`,
                    getStyle: getSelectedStyle,
                    // getStyle: layerId => {
                    //     //TODO
                    //     switch (layerId) {
                    //         case 'issues-typology':
                    //             return some(issueStyleTypologySelect());
                    //         case 'issues-priority':
                    //             return some(issueStylePrioritySelect());
                    //         default:
                    //             return none;
                    //     }
                    // },
                }),
                getInteraction
            );

            andThen(m => {
                const view = new View({
                    projection: 'EPSG:3857',
                    center: DEFAULT_CENTER_3857,
                    rotation: 0,
                    zoom: 8,
                });
                m.setView(view); //override the view for overriding the projection
                const osm = new TileLayer({
                    source: new OSM(),
                    zIndex: 0,
                });
                m.addLayer(osm);
            });

            return some(update);
        },
        update => some(update)
    );

    if (element) {
        mapSetTarget.map(f => f(element));
    }
};

const renderAutoZoomCheckBox = () =>
    DIV(
        { className: 'zoom-auto checkbox', onClick: () => changeAutoZoom() },
        tr.qa('autoZoom')
    );

const renderAutoZoomCheckBoxSelected = () =>
    DIV(
        {
            className: 'zoom-auto checkbox selected',
            onClick: () => changeAutoZoom(),
        },
        tr.qa('autoZoom')
    );

const renderAutoCenterCheckBox = () =>
    DIV(
        {
            className: 'center-auto checkbox',
            onClick: () => setAutoCenter(true),
        },
        tr.qa('autoCenter')
    );

const renderAutoCenterCheckBoxSelected = () =>
    DIV(
        {
            className: 'center-auto checkbox selected',
            onClick: () => setAutoCenter(false),
        },
        tr.qa('autoCenter')
    );

const renderMapButton = () =>
    divTooltipLeft(tr.qa('biggerMap'), {}, mapButton(toggleRenderMap));

const renderMapAndTableButton = () =>
    divTooltipLeft(
        tr.qa('showMapAndTable'),
        {},
        mapAndTableButton(toggleRenderMapAndTable)
    );

const render = () => {
    mapUpdate.map(f => f());
    return DIV(
        { className: 'map-wrapper' },
        DIV(
            { className: 'navigation-options' },
            getAutoZoom()
                ? renderAutoZoomCheckBoxSelected()
                : renderAutoZoomCheckBox(),
            getAutoCenter()
                ? renderAutoCenterCheckBoxSelected()
                : renderAutoCenterCheckBox()
        ),
        //DIV({ className: 'layer-select' }, baseLayerSwitch()),
        DIV(
            { className: 'layout-select' },
            getLayout() !== 'map' ? renderMapButton() : NODISPLAY(),
            getLayout() !== 'mapAndTable'
                ? renderMapAndTableButton()
                : NODISPLAY()
        ),

        DIV({
            id: `qa-map ${getTheme().getOrElse('')}`,
            className: 'map',
            ref: attachMap,
        })
    );
};

export default render;
